import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output, HostListener, ViewChild } from '@angular/core';
import { AttachmentEntity } from 'app/models/attachment-entity.model';
import { AttachmentResponse, BulkUploadAttachmentResponse } from 'app/models/attachment-response.model';
import { BulkAttachmentUploadService } from 'app/service/bulk-attachment-upload.service';
import { FileCaptureService } from 'app/service/file-capture.service';
import { FileServiceService } from 'app/service/file-service.service';
import { environment } from 'environments/environment';

@Component({
  standalone: false,
  selector: 'app-optimized-file-upload',
  templateUrl: './optimized-file-upload.component.html',
  styleUrls: ['./optimized-file-upload.component.css']
})
export class OptimizedFileUploadComponent implements OnInit, OnDestroy {

  private readonly UPLOAD_CHUNK_SIZE_BYTES = 100 * 1024 * 1024;
  private readonly MAX_CONCURRENT_CHUNKS = 4;
  private readonly MAX_CONCURRENT_FILES = 3;
  private readonly MAX_UPLOAD_SLOTS = 4; // slot weights: small=1, medium=2, large=4
  private pendingTransferResolvers = new Map<string, () => void>();
  private batchUploadStartedAt: number | null = null;

  FilesToUpload: any = [];
  fileUploadMap: any = {};
  private chunkBytesMap = new Map<any, Map<number, number>>(); // tracks in-flight bytes per chunk for accurate progress
  totalUploadedFiles: any = 0;
  canUpload: boolean = true;
  hasUpdate: boolean;
  statusPollingTimer: any = null;
  isPollRequestInFlight: boolean = false;
  uploadCompletionTimer: any = null;
  hasUploadErrors: boolean = false;

  showReUploadModal: boolean = false;
  reUploadFilesList: any[] = [];
  reUploadError: string = '';
  reUploadNewFiles: File[] = [];

  @ViewChild('reUploadFileInput') reUploadFileInput: ElementRef | undefined;

  @Input() files: any[] = [];
  @Input() uploadedFiles: File[] | null = null;
  @Output() uploadCompleted = new EventEmitter<any>();
  @Output() uploadClosed = new EventEmitter<void>();

  constructor(
    public service: FileServiceService,
    private fcservice: FileCaptureService,
    private bulkAttachmentUploadService: BulkAttachmentUploadService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.startBulkUpload();
  }

  ngOnDestroy(): void {
    this.stopStatusPolling();

    if (this.uploadCompletionTimer) {
      clearInterval(this.uploadCompletionTimer);
      this.uploadCompletionTimer = null;
    }
  }

  private logBatchUploadStart(newFiles: any[]) {
    this.batchUploadStartedAt = Date.now();
    const totalSizeBytes = (newFiles || []).reduce((sum: number, file: any) => sum + Number(file?.fileSize || 0), 0);
    const totalSizeMB = (totalSizeBytes / (1024 * 1024)).toFixed(2);
    console.log(`[OptimizedFileUpload][BATCH-UPLOAD-START] Files: ${(newFiles || []).length} | Total size: ${totalSizeMB} MB | Started at: ${new Date(this.batchUploadStartedAt).toISOString()}`);
  }

  private logBatchUploadEnd(activeUploads: any[]) {
    if (!this.batchUploadStartedAt) {
      return;
    }

    const durationMs = Date.now() - this.batchUploadStartedAt;
    const durationSec = Math.max(durationMs / 1000, 0.001);
    const totalSizeBytes = (activeUploads || []).reduce((sum: number, file: any) => sum + Number(file?.fileSize || 0), 0);
    const totalSizeMB = totalSizeBytes / (1024 * 1024);
    const successCount = (activeUploads || []).filter((file: any) => file.scanStatus === 1).length;
    const failedCount = (activeUploads || []).filter((file: any) =>
      file.scanStatus === 2 || file.scanStatus === 99 ||
      file.uploadFailed === true ||
      Number(file.percentage) === 500 ||
      Number(file.percentage) === 600
    ).length;

    console.log(
      `[OptimizedFileUpload][BATCH-UPLOAD-END] Files: ${(activeUploads || []).length} | Success: ${successCount} | Failed: ${failedCount} | Total size: ${totalSizeMB.toFixed(2)} MB | Duration: ${durationSec.toFixed(2)}s | Avg speed: ${(totalSizeMB / durationSec).toFixed(2)} MB/s | Completed at: ${new Date().toISOString()}`
    );

    this.batchUploadStartedAt = null;
  }

  private isInvalidAttachmentKey(data: any): boolean {
    return data?.attachmentKey === -1 || data?.attachmentKey === 0;
  }

  async bulkUploadFiles(files: any, deferUpload: boolean = false, cachedCaseActivityKey: any = 0) {
    const attachments: any[] = [];
    for (let i = 0; i < files.length; i++) {
      const attachment: any = {
        fileName: files[i].fileName.substring(files[i].fileName.lastIndexOf('\\') + 1),
        fileType: files[i].fileName.substring(files[i].fileName.lastIndexOf('.') + 1),
        percentage: 0,
        fileSize: files[i].fileSize || 0,
        sizeReadable: files[i].sizeReadable || '',
        isFirst: i === 0,
        fileId: i + 1,
        activityName: 'Deconfliction',
        referenceId: this.service.getCache('referenceId'),
        targetFile: this.uploadedFiles?.find(x => x.name === files[i].fileName.substring(files[i].fileName.lastIndexOf('\\') + 1)),
        attachmentLength: files[i].fileSize,
        noOfchunksSent: 0,
        bufferChunkSize: 0,
        uploadStatus: 'uploading',
        statusText: 'Upload in progress',
        failureToUpload: false
      };

      this.FilesToUpload.push(attachment);
      attachments.push(attachment);
    }
    this.cdr.detectChanges();

    await this.addAttachmentsToDbBulkAsync(attachments, deferUpload, cachedCaseActivityKey || 0);

    return attachments;
  }

  async startBulkUpload() {
    const newFiles = this.files.filter((x: any) => x.isUpload == 1);

    if ((!this.uploadedFiles || this.uploadedFiles.length === 0) &&
      this.files.some((f: any) => f.attachmentKey)) {
      this.restoreAfterReload();
      return;
    }

    if (newFiles.length === 0) {
      if (!this.hasErroredFiles) {
        this.uploadCompleted.emit({
          files: this.files,
          hasInvalidFiles: false,
          hasUploadErrors: false
        });
      }

      return;
    }

    newFiles.forEach((file: any) => {
      file.uploadFailed = false;
      file.scanStatus = -1; // pending
      file.uploadStatus = 'uploading';
      file.statusText = 'Upload in progress';
    });

    this.logBatchUploadStart(newFiles);


    try {
      const attachmentSaved = this.service.getCache('attachmentSaved');
      const cachedCaseActivityKey = this.service.getCache('caseActivityKey');
      const currentReferenceId = this.service.getCache('referenceId');
      const attachmentSavedForRef = this.service.getCache('attachmentSavedForRef');
      const uploadStateIsValid = attachmentSaved && cachedCaseActivityKey && attachmentSavedForRef === currentReferenceId;

      if (!uploadStateIsValid) {

        let attachments: any[] = [];

        if (newFiles.length > 0) {
          attachments = await this.bulkUploadFiles(newFiles, true, 0);
        }

        if (attachments.length > 0) {
          await this.uploadWithDynamicConcurrency(attachments);
        }
      } else {
        const preparedAttachments = await this.bulkUploadFiles(newFiles, true, cachedCaseActivityKey || 0);
        if (preparedAttachments.length > 0) {
          await this.uploadWithDynamicConcurrency(preparedAttachments);
        }
      }

      this.startStatusPolling();

      this.waitForUploadCompletion();
    } catch (err) {
      this.waitForUploadCompletion();
    }
  }

  private addAttachmentsToDbBulkAsync(attachments: AttachmentEntity[], deferUpload: boolean = false, cachedCaseActivityKey: any = 0): Promise<void> {
    if (!attachments || attachments.length === 0) {
      return Promise.resolve();
    }

    const referenceId = this.service.getCache('referenceId') || attachments[0]?.referenceId || '';

    attachments.forEach((attachment: any) => {
      const idx = this.FilesToUpload.findIndex((x: any) => x.fileId === attachment.fileId);
      if (idx > -1) {
        this.FilesToUpload[idx].attachmentLength = attachment.fileSize;
        this.FilesToUpload[idx].targetFile = attachment.targetFile;
        this.FilesToUpload[idx].referenceId = referenceId;
      }
    });

    return new Promise<void>((resolve) => {
      this.bulkAttachmentUploadService.addAttachmentsToDBBulk(attachments, referenceId, cachedCaseActivityKey || 0).subscribe({
        next: (bulkResponse: BulkUploadAttachmentResponse) => {
          const responseList: AttachmentResponse[] = bulkResponse?.attachmentResponseList || [];
          let resolvedCaseActivityKey: any = null;

          const responseByFileName = new Map<string, AttachmentResponse>();
          (responseList || []).forEach((responseItem: AttachmentResponse) => {
            const responseFileName = (responseItem?.fileName || '').toLowerCase();
            if (responseFileName) {
              responseByFileName.set(responseFileName, responseItem);
            }
          });

          const validFileNamesForTransfer: string[] = [];
          const validFileIdsForTransfer: number[] = [];

          attachments.forEach((attachment: any) => {
            const fileNameKey = (attachment.fileName || '').toLowerCase();
            const responseData = responseByFileName.get(fileNameKey);
            const uploadIdx = this.FilesToUpload.findIndex((x: any) => x.fileId === attachment.fileId);

            if (!responseData || this.isInvalidAttachmentKey(responseData)) {
              if (uploadIdx > -1) {
                this.FilesToUpload[uploadIdx].uploadStatus = 'failed';
                this.FilesToUpload[uploadIdx].statusText = 'Error in upload';
                this.FilesToUpload[uploadIdx].failureToUpload = true;
                this.FilesToUpload[uploadIdx].uploadFailed = true;
                this.FilesToUpload[uploadIdx].percentage = 600;
              }

              const uiIdx = this.files.findIndex((x: any) => x.fileName === attachment.fileName);
              if (uiIdx > -1) {
                this.files[uiIdx].attachmentKey = responseData?.attachmentKey;
                this.files[uiIdx].isFirst = false;
                this.files[uiIdx].isUpload = 0;
                this.files[uiIdx].isVisible = (responseData?.attachmentKey || 0) + '_99';
                this.files[uiIdx].uploadFailed = true;
                this.files[uiIdx].percentage = 600;
                this.files[uiIdx].uploadStatus = 'failed';
                this.files[uiIdx].statusText = 'Error in upload';
              }
            }
            else {
              if (uploadIdx > -1) {
                this.FilesToUpload[uploadIdx].attachmentKey = responseData.attachmentKey;

                const responseCaseActivityKey = responseData.caseActivityKey;

                if (responseCaseActivityKey) {
                  this.FilesToUpload[uploadIdx].caseActivityKey = responseCaseActivityKey;
                  this.FilesToUpload[uploadIdx].serverCaseActivityKey = responseCaseActivityKey;
                }

                if (!resolvedCaseActivityKey && responseCaseActivityKey) {
                  resolvedCaseActivityKey = responseCaseActivityKey;
                }

                const uiIdx = this.files.findIndex((x: any) => x.fileName === this.FilesToUpload[uploadIdx].fileName);
                if (uiIdx > -1) {
                  this.files[uiIdx].attachmentKey = responseData.attachmentKey;
                  this.files[uiIdx].caseActivityKey = this.FilesToUpload[uploadIdx].caseActivityKey;
                  this.files[uiIdx].serverCaseActivityKey = this.FilesToUpload[uploadIdx].serverCaseActivityKey;
                  this.files[uiIdx].isFirst = false;
                  this.files[uiIdx].isUpload = 0;
                  this.files[uiIdx].isVisible = responseData.attachmentKey + '_0';
                }

                validFileNamesForTransfer.push(attachment.fileName);
                validFileIdsForTransfer.push(attachment.fileId);
              }
            }
          });

          if (validFileIdsForTransfer.length > 0) {
            if (resolvedCaseActivityKey) {
              this.service.referenceFileDetails.caseActivityKey = resolvedCaseActivityKey;
              this.service.setCacheForitems([{ id: 'caseActivityKey', value: resolvedCaseActivityKey }]);
            }

            this.service.setCacheForitems([
              { id: 'attachmentSaved', value: 'Saved' },
              { id: 'attachmentSavedForRef', value: referenceId }
            ]);
          }

          this.service.setCacheForitems([{ id: 'files', value: this.files }]);
          this.cdr.detectChanges();

          if (deferUpload) {
            resolve();
            return;
          }

          validFileIdsForTransfer.forEach((fileId: number) => this.processFiles(fileId));

          if (!validFileNamesForTransfer.length) {
            resolve();
            return;
          }

          Promise.all(validFileNamesForTransfer.map((fileName: string) => this.waitForTransferCompletion(fileName)))
            .then(() => resolve())
            .catch(() => resolve());
        },
        error: (err: any) => {
          attachments.forEach((attachment: any) => this.markFileUploadError(attachment.fileName));
          resolve();
        }
      });
    });
  }

  private getFileSlotCost(fileSizeBytes: number): number {
    const sizeMB = (fileSizeBytes || 0) / (1024 * 1024);
    if (sizeMB < 100) return 1;   // small: up to 4 concurrent
    if (sizeMB <= 500) return 2;  // medium: up to 2 concurrent
    return 4;                      // large: 1 at a time
  }

  private uploadWithDynamicConcurrency(attachments: any[]): Promise<void> {
    // small files first so slots fill more efficiently
    const fileQueue = [...attachments].sort(
      (a, b) => this.getFileSlotCost(a.fileSize) - this.getFileSlotCost(b.fileSize)
    );

    let usedSlots = 0;
    let finishedCount = 0;

    return new Promise<void>((resolve) => {

      const startNextFiles = () => {
        while (fileQueue.length > 0) {
          const slotsNeeded = this.getFileSlotCost(fileQueue[0].fileSize);

          if (usedSlots + slotsNeeded > this.MAX_UPLOAD_SLOTS) {
            break; // no room right now — wait for a running file to finish
          }

          const file = fileQueue[0];
          fileQueue.splice(0, 1);
          usedSlots += slotsNeeded;

          this.uploadPreparedAttachment(file).then(() => {
            usedSlots -= slotsNeeded;
            finishedCount++;
            if (finishedCount === attachments.length) {
              resolve(); // all files done
            } else {
              startNextFiles(); // a slot just freed — start the next file
            }
          }).catch(() => {
            usedSlots -= slotsNeeded;
            finishedCount++;
            if (finishedCount === attachments.length) {
              resolve();
            } else {
              startNextFiles();
            }
          });
        }
      };

      startNextFiles(); // kick off the first wave of uploads
    });
  }

  private async uploadPreparedAttachments(attachments: any[]): Promise<void> {
    for (let i = 0; i < attachments.length; i += this.MAX_CONCURRENT_FILES) {
      const batch = attachments.slice(i, i + this.MAX_CONCURRENT_FILES);
      await Promise.all(batch.map((attachment: any) => this.uploadPreparedAttachment(attachment)));
    }
  }

  private async uploadPreparedAttachment(attachment: any): Promise<void> {
    this.processFiles(attachment.fileId);
    await this.waitForTransferCompletion(attachment.fileName);
  }

  processFiles(i) {
    const getFileIndex = this.FilesToUpload.findIndex(x => x.fileId === i);
    if (getFileIndex === -1) {
      return;
    }

    if (this.FilesToUpload[getFileIndex].attachmentKey && Math.abs(parseInt(this.FilesToUpload[getFileIndex].fileSize)) >= this.UPLOAD_CHUNK_SIZE_BYTES) {
      const file = this.FilesToUpload[getFileIndex];
      file._uploadStartTime = Date.now();
      const fileSizeMB = (file.fileSize / (1024 * 1024)).toFixed(1);
      console.log(`[OptimizedFileUpload][FILE-UPLOAD-START] "${file.fileName}" | Size: ${fileSizeMB} MB | Started at: ${new Date().toISOString()}`);
      this.createandUploadChunks(file, file.targetFile, i);
      this.nextBatchProcess(i);
    }
    else {
      if (!this.FilesToUpload[getFileIndex].targetFile) {
        this.markFileUploadError(this.FilesToUpload[getFileIndex].fileName);
        return;
      }

      if (!this.FilesToUpload[getFileIndex].attachmentKey) {
        this.markFileUploadError(this.FilesToUpload[getFileIndex].fileName);
        return;
      }

      const smallFile = this.FilesToUpload[getFileIndex];
      smallFile._uploadStartTime = Date.now();
      console.log(`[OptimizedFileUpload][FILE-UPLOAD-START] "${smallFile.fileName}" | Size: ${(smallFile.fileSize / (1024 * 1024)).toFixed(1)} MB | Started at: ${new Date().toISOString()}`);

      this.upload(this.FilesToUpload[getFileIndex].targetFile, i, null, this.FilesToUpload[getFileIndex].fileSize, this.FilesToUpload[getFileIndex].fileName,
        0, this.FilesToUpload[getFileIndex].attachmentKey, i);
    }
  }

  createandUploadChunks(attachment, targetFile, i) {
    var fileChunks = [];
    var Size = attachment.fileSize;
    var num = this.UPLOAD_CHUNK_SIZE_BYTES;
    if (Size / num > 256) {
      attachment.bufferChunkSize = Size / 256;
    }
    else
      attachment.bufferChunkSize = this.UPLOAD_CHUNK_SIZE_BYTES;
    var ReadBuffer_Size = 1024;
    var FileStreamPos = 0;
    var EndPos = attachment.bufferChunkSize;

    var fileChunkId = 0;
    while (FileStreamPos < Size) {
      if (!targetFile) {
        this.markFileUploadError(attachment.fileName);
        return;
      }
      fileChunks.push({ targetFile: targetFile.slice(FileStreamPos, EndPos), id: fileChunkId });
      FileStreamPos = EndPos;
      EndPos = FileStreamPos + attachment.bufferChunkSize;
      fileChunkId++;
    }
    var getFileIndex = this.FilesToUpload.findIndex(x => x.fileId === i);

    this.FilesToUpload[getFileIndex].fileChunksLength = fileChunks.length;
    this.FilesToUpload[getFileIndex].fileChunks = fileChunks;
    this.FilesToUpload[getFileIndex].lastFileChunkId = fileChunkId;
    console.log(`[OptimizedFileUpload][CHUNKS-PREPARED] "${attachment.fileName}" | Total size: ${(attachment.fileSize / (1024 * 1024)).toFixed(1)} MB | Chunks: ${fileChunks.length} | Chunk size: ${(attachment.bufferChunkSize / (1024 * 1024)).toFixed(1)} MB`);
  }

  nextBatchProcess(i) {
    const getFileIndex = this.FilesToUpload.findIndex(x => x.fileId === i);
    const file = this.FilesToUpload[getFileIndex];
    const allChunks = [...file.fileChunks];
    let chunkIndex = 0;
    let activeCount = 0;

    console.log(`[OptimizedFileUpload][CONCURRENT-UPLOAD] "${file.fileName}" | ${allChunks.length} chunks | Max concurrent: ${this.MAX_CONCURRENT_CHUNKS}`);

    const uploadNext = () => {
      // Stop queueing new chunks if file already errored
      const currentFile = this.FilesToUpload[getFileIndex];
      if (currentFile?.uploadFailed || currentFile?.failureToUpload) {
        return;
      }
      while (activeCount < this.MAX_CONCURRENT_CHUNKS && chunkIndex < allChunks.length) {
        const chunk = allChunks[chunkIndex++];
        if (chunk != null) {
          activeCount++;
          console.log(`[OptimizedFileUpload][CHUNK-SENDING] "${file.fileName}" | Chunk ${chunk.id + 1}/${file.fileChunksLength} | Active: ${activeCount}`);
          this.upload(
            chunk.targetFile, chunk.id, null,
            file.fileSize, file.fileName, file.fileChunksLength,
            file.attachmentKey, i,
            () => { activeCount--; uploadNext(); }
          );
        }
      }
    };

    uploadNext();
  }

  upload(chunk, chunkorfileId, objXhr1, fileSize, fileName, totalChunks, attachmentKey, itemindex, onComplete?: () => void) {
    try {
      if (fileSize <= this.UPLOAD_CHUNK_SIZE_BYTES) {
        chunkorfileId = this.FilesToUpload.findIndex(x => x.fileId === chunkorfileId);
      }
      var baseUrl = environment.baseUri;
      const uploadFile = this.FilesToUpload.find((x: any) => x.fileId === itemindex) || this.FilesToUpload.find((x: any) => x.attachmentKey === attachmentKey);
      const caseActivityKeyForUpload = uploadFile?.caseActivityKey || this.service.getCache('caseActivityKey') || this.service.referenceFileDetails.caseActivityKey;
      if (caseActivityKeyForUpload) {
        this.service.referenceFileDetails.caseActivityKey = caseActivityKeyForUpload;
      }
      const sessionId = window.sessionStorage.getItem(this.fcservice.SESSION_KEY) || '';
      var url =
        baseUrl + "FileCapture/UploadAttachment?attachmentKey=" + attachmentKey + "&referenceId=" + this.service.referenceFileDetails.referenceId + "&caseactivitykey=" + caseActivityKeyForUpload + "&sessionId=" + encodeURIComponent(sessionId);

      var objXhr;
      objXhr = new XMLHttpRequest();
      var data = new FormData();

      data.append('uploadedFile', chunk);
      objXhr.upload.fileId = chunkorfileId;
      objXhr.upload.fileName = fileName;
      objXhr.upload.attachmentKey = attachmentKey;

      objXhr.timeout = 3600000;
      objXhr.open('POST', url);

      objXhr.onerror = () => {
        this.zone.run(() => {
          this.markFileUploadError(fileName);
        });
      };

      objXhr.ontimeout = () => {
        this.zone.run(() => {
          this.markFileUploadError(fileName);
        });
      };

      objXhr.setRequestHeader("X-Content-Name", fileName);
      objXhr.setRequestHeader("Ocp-Apim-Subscription-Key", environment.subscriptionKey);
      if (totalChunks !== 0) {

        objXhr.setRequestHeader("X-Chunk-Id", chunkorfileId);
        objXhr.setRequestHeader("X-Content-Length", fileSize);
        objXhr.setRequestHeader("X-Total-Chunks", totalChunks);

        if (chunkorfileId === totalChunks - 1) {
          objXhr.setRequestHeader("X-Last-Chunk", "true");
        }
        else
          objXhr.setRequestHeader("X-Last-Chunk", "false");
      }

      if (fileSize <= this.UPLOAD_CHUNK_SIZE_BYTES) {
        this.FilesToUpload[chunkorfileId].xmlObj = objXhr;
        objXhr.upload.addEventListener("progress", (evt) => this.updateProgressForSmallFiles(evt), false);
      }
      else
        objXhr.upload.addEventListener("progress", (evt) => this.updateProgress(evt), false);

      objXhr.onload = () => {
        this.zone.run(() => {
          if (objXhr.status < 200 || objXhr.status >= 300) {
            this.markFileUploadError(fileName);
            return;
          }

          if (fileSize > this.UPLOAD_CHUNK_SIZE_BYTES) {
            if (this.fileUploadMap[attachmentKey] === undefined) {
              this.fileUploadMap[attachmentKey] = 1;
            } else {
              this.fileUploadMap[attachmentKey]++;
            }
            // chunk is server-confirmed — remove its in-flight bytes so completedBytes takes over
            this.chunkBytesMap.get(attachmentKey)?.delete(chunkorfileId);
            console.log(`[OptimizedFileUpload][CHUNK-DONE] "${fileName}" | Chunk ${this.fileUploadMap[attachmentKey]}/${totalChunks} received`);
            if (onComplete) onComplete();

            if (totalChunks === this.fileUploadMap[attachmentKey]) {
              this.totalUploadedFiles++;

              this.fcservice.commitFile(attachmentKey, fileName, totalChunks).subscribe({
                next: () => {
                  const uploadedFile = this.FilesToUpload.find((x: any) => x.attachmentKey === attachmentKey);
                  if (uploadedFile?._uploadStartTime) {
                    const durationMs = Date.now() - uploadedFile._uploadStartTime;
                    const durationSec = (durationMs / 1000).toFixed(1);
                    const fileSizeMB = (uploadedFile.fileSize / (1024 * 1024)).toFixed(1);
                    console.log(`[OptimizedFileUpload][FILE-UPLOAD-END] "${fileName}" | Size: ${fileSizeMB} MB | Duration: ${durationSec}s | Avg speed: ${((uploadedFile.fileSize / (1024 * 1024)) / (durationMs / 1000)).toFixed(2)} MB/s | Completed at: ${new Date().toISOString()}`);
                  }
                  this.updateFileSuccess(fileName);
                },
                error: () => {
                  this.markFileUploadError(fileName);
                }
              });
            }
          } else {
            this.totalUploadedFiles++;
            if (onComplete) onComplete();
            const uploadedSmallFile = this.FilesToUpload.find((x: any) => x.attachmentKey === attachmentKey);
            if (uploadedSmallFile?._uploadStartTime) {
              const durationMs = Date.now() - uploadedSmallFile._uploadStartTime;
              console.log(`[OptimizedFileUpload][FILE-UPLOAD-END] "${fileName}" | Size: ${(uploadedSmallFile.fileSize / (1024 * 1024)).toFixed(1)} MB | Duration: ${(durationMs / 1000).toFixed(1)}s | Avg speed: ${((uploadedSmallFile.fileSize / (1024 * 1024)) / (durationMs / 1000)).toFixed(2)} MB/s | Completed at: ${new Date().toISOString()}`);
            }
            this.updateFileSuccess(fileName);
          }
        });
      };

      objXhr.send(data);
    }
    catch (e) {
      const result = (e as Error).message as string;

      if (result.includes('String contains non ISO-8859-1 code point')) {
        this.markFileInvalid(fileName);
      } else {
        this.markFileUploadError(fileName);
      }
    }
  }

  updateProgressForSmallFiles(e: any) {
    const filename = e.currentTarget.fileName;
    let keyOfFilesmall: any;

    this.FilesToUpload.forEach((value: any, key: any) => {
      if (value.fileName === filename) {
        keyOfFilesmall = key;
      }
    });

    this.zone.run(() => {
      const percent = e.lengthComputable ? Math.min(Math.round((e.loaded / e.total) * 100), 99) : this.FilesToUpload[keyOfFilesmall].percentage;
      this.FilesToUpload[keyOfFilesmall].percentage = percent;

      const index = this.files.findIndex((x: any) => x.fileName === this.FilesToUpload[keyOfFilesmall].fileName);
      if (index > -1) {
        this.files[index].percentage = percent;
      }

      this.cdr.detectChanges();
    });
  }

  updateProgress(e: any) {
    if (!e.lengthComputable) return;

    var chunkId: number = e.currentTarget.fileId;
    var filename: string = e.currentTarget.fileName;
    var attachmentKey = e.currentTarget.attachmentKey;

    let keyOfFile;
    this.FilesToUpload.forEach((value, key) => {
      if (value.fileName === filename) {
        keyOfFile = key;
      }
    });

    var file = this.FilesToUpload[keyOfFile];

    // Update bytes uploaded for this specific chunk
    if (!this.chunkBytesMap.has(attachmentKey)) {
      this.chunkBytesMap.set(attachmentKey, new Map());
    }
    this.chunkBytesMap.get(attachmentKey)!.set(chunkId, e.loaded);

    // Sum in-flight bytes across all concurrent chunks
    var activeBytes = 0;
    this.chunkBytesMap.get(attachmentKey)!.forEach(bytes => { activeBytes += bytes; });

    // Add bytes from server-confirmed chunks
    var confirmedChunks: number = this.fileUploadMap[attachmentKey] || 0;
    var confirmedBytes = confirmedChunks * file.bufferChunkSize;
    var perc = Math.min(Math.round(((confirmedBytes + activeBytes) / file.fileSize) * 100), 99);

    this.zone.run(() => {
      if (perc > (file.percentage || 0)) {
        file.percentage = perc;
        var index = this.files.findIndex((x: any) => x.fileName === filename);
        if (index > -1) {
          this.files[index].percentage = perc;
        }
        this.cdr.detectChanges();
      }
    });
  }

  startStatusPolling() {
    if (this.statusPollingTimer) {
      clearInterval(this.statusPollingTimer);
    }

    this.isPollRequestInFlight = false;

    this.statusPollingTimer = setInterval(() => {
      if (this.isPollRequestInFlight) {
        return;
      }

      // Wait until all file transfers are complete/failed before polling scan status
      if (this.hasPendingFileTransfer()) {
        return;
      }

      // Only poll files that were successfully transferred but don't yet have a final scan result
      // Final statuses: 1=passed, 2=threat found, 99=technical failure; 0=still scanning
      const pendingScanFiles = this.files.filter((x: any) => {
        const hasKey = !!x.attachmentKey;
        const transferCompleted = x.uploadStatus === 'complete' &&
          x.statusText === 'Upload complete' && x.isReadyToDownload === true &&
          x.failureToUpload === false;
        const hasFinalScanStatus = x.scanStatus === 1 || x.scanStatus === 2 || x.scanStatus === 99;
        return hasKey && transferCompleted && !hasFinalScanStatus;
      });

      // All successfully uploaded files have a final scan result — stop polling
      if (pendingScanFiles.length === 0) {
        this.stopStatusPolling();
        return;
      }

      const attachment = {
        referenceId: this.service.getCache('referenceId'),
        attachmentKeyList: pendingScanFiles.map((x: any) => x.attachmentKey)
      };

      this.isPollRequestInFlight = true;

      this.fcservice.GetAttachmentStatus(attachment).subscribe({
        next: (status: any) => {
          if (status?.length) {
            this.files.forEach((item: any) => {
              const matched = status.find((y: any) => y.id == item.attachmentKey);
              if (!matched) {
                return;
              }

              const scanStatus = matched.VirusScanStatus ?? 0;
              item.scanStatus = scanStatus;
              item.isVisible = item.attachmentKey + '_' + scanStatus;

              if (scanStatus === 1) {
                // Scan passed — no threats found
                item.percentage = 100;
                item.uploadFailed = false;
                item.isReadyToDownload = true;
                item.uploadStatus = 'complete';
                item.statusText = 'Upload complete';
                item.isuploaded = true;
              } else if (scanStatus === 2) {
                // Threat found
                item.percentage = 500;
                item.uploadFailed = false;
                item.uploadStatus = 'failed';
                item.statusText = 'Error in upload';
              } else if (scanStatus === 99) {
                // Technical failure reading scan result
                item.percentage = 500;
                item.uploadFailed = false;
                item.uploadStatus = 'failed';
                item.statusText = 'Error in upload';
              }
              // scanStatus === 0 means scan still in progress — leave state unchanged and keep polling

              const uploadFileMatch = this.FilesToUpload.find((f: any) => f.fileName === item.fileName);
              if (uploadFileMatch) {
                uploadFileMatch.scanStatus = scanStatus;
                if (scanStatus === 1) {
                  uploadFileMatch.percentage = 100;
                  uploadFileMatch.uploadFailed = false;
                  uploadFileMatch.uploadStatus = 'complete';
                  uploadFileMatch.statusText = 'Upload complete';
                } else if (scanStatus === 2 || scanStatus === 99) {
                  uploadFileMatch.percentage = 500;
                  uploadFileMatch.uploadFailed = false;
                  uploadFileMatch.uploadStatus = 'failed';
                  uploadFileMatch.statusText = 'Error in upload';
                }
              }
            });

            this.service.setCacheForitems([{ id: 'files', value: this.files }]);
            this.cdr.detectChanges();
          }

            const allFinal = this.files
            .filter((x: any) => x.attachmentKey)
            .every((item: any) => {
              return (
                item.scanStatus === 1 ||
                item.scanStatus === 99 ||
                item.scanStatus === 2 ||
                item.uploadFailed === true ||
                item.percentage === 500 ||
                item.percentage === 600
              );
            });

          if (allFinal) {
            this.stopStatusPolling();
          }
        },
        error: (err) => {
          this.isPollRequestInFlight = false;
        },
        complete: () => {
          this.isPollRequestInFlight = false;
        }
      });
    }, 3000);
  }

  private hasPendingFileTransfer(): boolean {
    return this.files.some((x: any) => {
      const transferFailed =
        x.uploadFailed === true ||
        x.uploadStatus === 'failed' ||
        Number(x.percentage) === 600;

      const transferComplete = x.uploadStatus === 'complete' || transferFailed;

      // Explicit in-progress state
      if (x.uploadStatus === 'uploading') {
        return true;
      }

      // Upload requested but key not assigned yet
      if (!x.attachmentKey && x.isUpload === 1 && !transferFailed) {
        return true;
      }

      // Key exists but transfer not complete/failed yet
      if (x.attachmentKey && !transferComplete) {
        return true;
      }

      return false;
    });
  }

  stopStatusPolling() {
    if (this.statusPollingTimer) {
      clearInterval(this.statusPollingTimer);
      this.statusPollingTimer = null;
    }
  }

  waitForUploadCompletion() {
    if (this.uploadCompletionTimer) {
      clearInterval(this.uploadCompletionTimer);
    }

    this.uploadCompletionTimer = setInterval(() => {
      const filesStillAwaitingUpload = this.files.some((item: any) =>
        !item.attachmentKey && item.uploadStatus === 'uploading' && item.uploadFailed !== true
      );
      if (filesStillAwaitingUpload) {
        return;
      }

      const activeUploads = this.files.filter((item: any) =>
        item.attachmentKey || item.uploadFailed === true || item.percentage === 600
      );

      if (!activeUploads.length) {
        return;
      }

      const filesStillTransferring = activeUploads.some((item: any) => {
        const isFinalError =
          item.uploadFailed === true ||
          item.percentage === 500 ||
          item.percentage === 600 ||
          item.scanStatus === 2 ||
          item.scanStatus === 99;

        // scanStatus=0 means scan still in progress — not a final state
        const transferCompleted =
          item.uploadStatus === 'complete' &&
          item.scanStatus === 1;

        return item.attachmentKey && !isFinalError && !transferCompleted;
      });

      if (filesStillTransferring) {
        return;
      }

      const allFinal = activeUploads.every((item: any) => {
        return (
          item.uploadStatus === 'failed' ||
          item.scanStatus === 1 ||
          item.scanStatus === 2 ||
          item.scanStatus === 99 ||
          item.uploadFailed === true ||
          item.percentage === 500 ||
          item.percentage === 600
        );
      });

      if (allFinal) {
        this.logBatchUploadEnd(activeUploads);
        clearInterval(this.uploadCompletionTimer);
        this.uploadCompletionTimer = null;
        this.stopStatusPolling();

        const invalidFilePresent = activeUploads.some((item: any) =>
          item.scanStatus === 2 || item.percentage === 500
        );

        const uploadErrorPresent = activeUploads.some((item: any) =>
          item.uploadFailed === true || item.percentage === 600
        );

        if (this.hasErroredFiles) {
          this.cdr.detectChanges();
          return;
        }

        this.uploadCompleted.emit({
          files: this.files,
          hasInvalidFiles: invalidFilePresent,
          hasUploadErrors: uploadErrorPresent
        });
        return;
      }
    }, 1000);
  }

  private markFileUploadError(fileName: string) {
    const uploadFile = this.FilesToUpload.find((x: any) => x.fileName === fileName);
    if (uploadFile) {
      uploadFile.uploadStatus = 'failed';
      uploadFile.statusText = 'Error in upload';
      uploadFile.failureToUpload = true;
      uploadFile.uploadFailed = true;
      uploadFile.isReadyToDownload = false;
      uploadFile.percentage = 600;
    }

    const file = this.files.find((x: any) => x.fileName === fileName);
    if (file) {
      file.uploadStatus = 'failed';
      file.statusText = 'Error in upload';
      file.failureToUpload = true;
      file.uploadFailed = true;
      file.isReadyToDownload = false;
      file.percentage = 600;
      file.isVisible = file.attachmentKey + '_99';
    }

    this.resolvePendingTransfer(fileName);
    this.service.setCacheForitems([{ id: 'files', value: this.files }]);
    this.cdr.detectChanges();
  }

  private markFileInvalid(fileName: string) {
    const uploadFile = this.FilesToUpload.find((x: any) => x.fileName === fileName);
    if (uploadFile) {
      uploadFile.uploadStatus = 'failed';
      uploadFile.statusText = 'Error in upload';
      uploadFile.failureToUpload = true;
      uploadFile.uploadFailed = false;
      uploadFile.scanStatus = 99;
      uploadFile.percentage = 500;
    }

    const file = this.files.find((x: any) => x.fileName === fileName);
    if (file) {
      file.uploadStatus = 'failed';
      file.statusText = 'Error in upload';
      file.failureToUpload = true;
      file.uploadFailed = false;
      file.scanStatus = 99;
      file.percentage = 500;
      file.isVisible = file.attachmentKey + '_99';
    }

    this.resolvePendingTransfer(fileName);
    this.service.setCacheForitems([{ id: 'files', value: this.files }]);
    this.cdr.detectChanges();
  }

  private updateFileSuccess(fileName: string) {
    const uploadFile = this.FilesToUpload.find((x: any) => x.fileName === fileName);
    if (uploadFile) {
      uploadFile.uploadStatus = 'complete';
      uploadFile.statusText = 'Upload complete';
      uploadFile.failureToUpload = false;
      uploadFile.isReadyToDownload = true;
    }

    const file = this.files.find((x: any) => x.fileName === fileName);
    if (file) {
      file.uploadStatus = 'complete';
      file.statusText = 'Upload complete';
      file.failureToUpload = false;
      file.isReadyToDownload = true;
      file.isuploaded = true;
    }

    this.resolvePendingTransfer(fileName);
    this.service.setCacheForitems([{ id: 'files', value: this.files }]);
    this.cdr.detectChanges();
  }

  private waitForTransferCompletion(fileName: string): Promise<void> {
    const file = this.files.find((x: any) => x.fileName === fileName);
    const transferFinished = !!file && (
      file.uploadStatus === 'complete' ||
      file.uploadStatus === 'failed' ||
      file.uploadFailed === true ||
      Number(file.percentage) === 500 ||
      Number(file.percentage) === 600
    );

    if (transferFinished) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      this.pendingTransferResolvers.set(fileName, resolve);
    });
  }

  private resolvePendingTransfer(fileName: string) {
    const resolver = this.pendingTransferResolvers.get(fileName);
    if (resolver) {
      this.pendingTransferResolvers.delete(fileName);
      resolver();
    }
  }

  getStatusText(file: any): string {
    if (file.scanStatus === 1) {
      return 'Complete';
    }

    if (
      file.uploadStatus === 'failed' ||
      file.uploadFailed === true ||
      file.scanStatus === 2 ||
      file.scanStatus === 99 ||
      Number(file.percentage) === 500 ||
      Number(file.percentage) === 600
    ) {
      return 'Error in Upload';
    }

    return 'In Progress';
  }

  get allFilesComplete(): boolean {
    if (!this.FilesToUpload || this.FilesToUpload.length === 0) {
      return false;
    }
    return this.FilesToUpload.every((f: any) => f.scanStatus === 1);
  }

  get hasErrorInUpload(): boolean {
    if (!this.FilesToUpload || this.FilesToUpload.length === 0) {
      return false;
    }

    const allFilesFinalized = this.FilesToUpload.every((f: any) => {
      const isConfirmedSuccess = f.scanStatus === 1;
      const isConfirmedError = f.scanStatus === 2 || f.scanStatus === 99 || Number(f.percentage) === 500 || Number(f.percentage) === 600 || f.uploadFailed === true;
      return isConfirmedSuccess || isConfirmedError;
    });

    if (!allFilesFinalized) {
      return false;
    }

    return this.FilesToUpload.some((f: any) => {
      return f.scanStatus === 2 || f.scanStatus === 99 || Number(f.percentage) === 500 || Number(f.percentage) === 600 || f.uploadFailed === true;
    });
  }

  removeAndGoBackToUploadPage() {
    const cleanFiles = this.FilesToUpload.filter((f: any) =>
      f.percentage !== 500 &&
      f.percentage !== 600 &&
      f.uploadFailed !== true &&
      f.failureToUpload !== true &&
      f.scanStatus !== 2 &&
      f.scanStatus !== 99
    );

    this.FilesToUpload = cleanFiles;
    this.files = cleanFiles;

    this.service.setCacheForitems([
      { id: 'files', value: cleanFiles }
    ]);

    this.uploadCompleted.emit({
      files: cleanFiles,
      hasInvalidFiles: false,
      hasUploadErrors: false
    });

    this.cdr.detectChanges();
  }

  openReUploadModal() {
    this.showReUploadModal = true;
    this.reUploadError = '';
    this.reUploadNewFiles = [];

    const successFiles = this.FilesToUpload.filter((f: any) =>
      f.percentage !== 500 &&
      f.percentage !== 600 &&
      f.uploadFailed !== true &&
      f.failureToUpload !== true &&
      f.scanStatus !== 2 &&
      f.scanStatus !== 99
    );

    this.reUploadFilesList = successFiles.map((f: any) => ({
      fileName: f.fileName,
      sizeReadable: f.sizeReadable,
      fileSize: f.fileSize,
      alreadyUploaded: true,
      attachmentKey: f.attachmentKey
    }));

    this.cdr.detectChanges();
  }

  onReUploadFileChange(event: any) {
    const fileList = event.files ? event.files : event;
    if (!fileList || fileList.length === 0) {
      return;
    }

    this.reUploadError = '';
    const incomingFiles = Array.from(fileList) as File[];

    for (let i = 0; i < incomingFiles.length; i++) {
      const file = incomingFiles[i];

      if (this.reUploadFilesList.some((f: any) => f.fileName === file.name)) {
        this.reUploadError += 'Duplicate file detected: ' + file.name + '. ';
        continue;
      }

      const totalFileSize = this.reUploadFilesList.reduce((total: number, f: any) => total + (f.fileSize || 0), 0);
      if ((totalFileSize + file.size) > 1.5 * 1024 * 1024 * 1024) {
        this.reUploadError += 'The total file size exceeds the limit of 1.5 GB. File Name: ' + file.name + '. ';
        continue;
      }

      this.reUploadNewFiles.push(file);
      this.reUploadFilesList.push({
        fileName: file.name,
        sizeReadable: this.humanFileSize(file.size, true),
        fileSize: file.size,
        alreadyUploaded: false,
        targetFile: file
      });
    }

    if (this.reUploadFileInput?.nativeElement) {
      this.reUploadFileInput.nativeElement.value = '';
    }

    this.cdr.detectChanges();
  }

  removeReUploadFile(file: any) {
    this.reUploadFilesList = this.reUploadFilesList.filter((f: any) => f !== file);
    this.reUploadNewFiles = this.reUploadNewFiles.filter((f: File) => f.name !== file.fileName);
    this.cdr.detectChanges();
  }

  submitReUpload() {
    this.showReUploadModal = false;

    const alreadyUploadedFiles = this.reUploadFilesList.filter((f: any) => f.alreadyUploaded);
    const newFilesToUpload = this.reUploadFilesList.filter((f: any) => !f.alreadyUploaded);

    this.files = this.files.filter((f: any) =>
      f.percentage !== 500 &&
      f.percentage !== 600 &&
      f.uploadFailed !== true &&
      f.failureToUpload !== true &&
      f.scanStatus !== 2 &&
      f.scanStatus !== 99
    );

    this.FilesToUpload = this.FilesToUpload.filter((f: any) =>
      f.percentage !== 500 &&
      f.percentage !== 600 &&
      f.uploadFailed !== true &&
      f.failureToUpload !== true &&
      f.scanStatus !== 2 &&
      f.scanStatus !== 99
    );

    if (newFilesToUpload.length === 0) {
      this.service.setCacheForitems([{ id: 'files', value: this.files }]);
      this.uploadCompleted.emit({
        files: this.files,
        hasInvalidFiles: false,
        hasUploadErrors: false
      });
      this.cdr.detectChanges();
      return;
    }

    const newFileEntries: any[] = [];
    const maxExistingFileId = this.FilesToUpload.length > 0
      ? Math.max(...this.FilesToUpload.map((f: any) => f.fileId))
      : -1;
    for (let i = 0; i < newFilesToUpload.length; i++) {
      const newFile = newFilesToUpload[i];
      const fileEntry = {
        fileName: newFile.fileName,
        fileType: newFile.fileName.substring(newFile.fileName.lastIndexOf('.') + 1),
        percentage: 0,
        fileSize: newFile.fileSize || 0,
        sizeReadable: newFile.sizeReadable || '',
        isFirst: false,
        fileId: maxExistingFileId + 1 + i,
        activityName: 'Deconfliction',
        referenceId: this.service.getCache('referenceId'),
        targetFile: newFile.targetFile,
        attachmentLength: newFile.fileSize,
        isUpload: 1,
        noOfchunksSent: 0,
        bufferChunkSize: 0,
        uploadStatus: 'uploading',
        statusText: 'Upload in progress',
        failureToUpload: false
      };
      newFileEntries.push(fileEntry);
      this.files.push(fileEntry);
    }

    if (this.uploadedFiles) {
      this.reUploadNewFiles.forEach((f: File) => {
        this.uploadedFiles!.push(f);
      });
    }

    this.service.setCacheForitems([{ id: 'files', value: this.files }]);
    this.totalUploadedFiles = 0;
    this.fileUploadMap = {};
    this.uploadReUploadFiles(newFileEntries);
    this.startStatusPolling();
    this.waitForUploadCompletion();

    this.cdr.detectChanges();
  }

  private async uploadReUploadFiles(newFileEntries: any[]) {
    const caseActivityKey = this.service.getCache('caseActivityKey');
    const attachmentsForBulk = newFileEntries.map((entry: any) => {
      const attachment = { ...entry };
      if (caseActivityKey) {
        attachment.caseActivityKey = caseActivityKey;
      }
      this.FilesToUpload.push(attachment);
      return attachment;
    });

    await this.addAttachmentsToDbBulkAsync(attachmentsForBulk, false, caseActivityKey || 0);
  }

  private restoreAfterReload(): void {

    this.files.forEach((file: any, index: number) => {
      const entry: any = {
        fileName: file.fileName,
        sizeReadable: file.sizeReadable || '',
        fileSize: file.fileSize || 0,
        fileId: index,
        attachmentKey: file.attachmentKey || null,
        uploadStatus: file.uploadStatus || 'uploading',
        statusText: file.statusText || 'Upload in progress',
        scanStatus: file.scanStatus || 0,
        percentage: file.percentage || 0,
        uploadFailed: file.uploadFailed || false,
        failureToUpload: file.failureToUpload || false,
        isReadyToDownload: file.isReadyToDownload || false,
        bufferChunkSize: 0,
        noOfchunksSent: 0
      };

      const wasInterrupted =
        !file.attachmentKey ||
        (
          !!file.attachmentKey &&
          Number(file.percentage || 0) < 100 &&
          file.scanStatus !== 1 &&
          file.scanStatus !== 2 &&
          file.scanStatus !== 99 &&
          file.uploadFailed !== true
        );

      if (wasInterrupted) {
        entry.uploadStatus = 'failed';
        entry.statusText = 'Error in upload';
        entry.uploadFailed = true;
        entry.failureToUpload = true;
        entry.percentage = 600;

        file.uploadStatus = 'failed';
        file.statusText = 'Error in upload';
        file.uploadFailed = true;
        file.failureToUpload = true;
        file.percentage = 600;
      }

      this.FilesToUpload.push(entry);
    });

    this.service.setCacheForitems([{ id: 'files', value: this.files }]);
    this.cdr.detectChanges();
    this.startStatusPolling();
    this.waitForUploadCompletion();
  }

  private humanFileSize(bytes: number, si: boolean): string {
    const thresh = si ? 1000 : 1024;
    if (Math.abs(bytes) < thresh) {
      return bytes + ' B';
    }
    const units = si
      ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
      : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
    let u = -1;
    do {
      bytes /= thresh;
      ++u;
    } while (Math.abs(bytes) >= thresh && u < units.length - 1);
    return bytes.toFixed(1) + ' ' + units[u];
  }

  get hasErroredFiles(): boolean {
    return this.FilesToUpload?.some((f: any) =>
      f.percentage === 500 ||
      f.percentage === 600 ||
      f.uploadFailed === true ||
      f.failureToUpload === true ||
      f.scanStatus === 2 ||
      f.scanStatus === 99
    );
  }

}
